/**
 * web/src/api/speakers.ts
 * Typed client for the speaker surfaces (server: src/pixel/api/speakers.py).
 * Flag-gated server-side (404 when off) + event-access-scoped. The monday board
 * is the source of truth; onboarding is ONE `speaker_onboarding` workflow per
 * speaker — a sequential head (invite → reply → confirmation → assets), then an
 * armed tail whose steps fire on their own triggers in any order.
 */
import { api } from '../lib/api';
import type { StartAt } from '../lib/speakerStartAt';
import type { SpeakerRosterEntry, SpeakerJourney } from './types';

/**
 * Lists the speaker roster, optionally filtered by event.
 *
 * @param eventId optional event filter (omit for the default speaker event)
 * @returns SpeakerRosterEntry[]
 * @throws ApiClientError on non-2xx (404 when the feature flag is off)
 * @sideEffects HTTP GET (reads the monday board live server-side)
 * @idempotent yes
 */
export async function fetchSpeakers(eventId?: string): Promise<SpeakerRosterEntry[]> {
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  const qs = params.toString();
  return api<SpeakerRosterEntry[]>(`/api/speakers${qs ? `?${qs}` : ''}`);
}

/**
 * Fetches one speaker's journey (monday record + THE onboarding workflow's
 * step states, projected onto the 9-step spine).
 *
 * @param eventId the speaker's event
 * @param itemId the monday item id
 * @returns SpeakerJourney
 * @throws ApiClientError on non-2xx (404 when off / speaker not on board)
 * @sideEffects HTTP GET (reads the monday board live server-side)
 * @idempotent yes
 */
export async function fetchSpeakerJourney(
  eventId: string,
  itemId: string,
): Promise<SpeakerJourney> {
  return api<SpeakerJourney>(
    `/api/speakers/${encodeURIComponent(eventId)}/${encodeURIComponent(itemId)}`,
  );
}

/**
 * The draft a date-armed email gate WILL open with (server:
 * ScheduledEmailPreviewOut) — rendered from the same snapshot + template the
 * gate build uses, so the preview matches the future gate exactly. `opensAt`
 * is the armed row's live date when one exists, else the plan computed from
 * the event's dates (naive-UTC ISO); null when the event has no dates yet.
 */
export interface ScheduledEmailPreview {
  stepId: string;
  stepName: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  bodyHtml: string;
  opensAt: string | null;
  firesTag: string | null;
}

/**
 * Fetches the read-only preview of a date-armed gate's email (logistics /
 * thank_you). Strictly read-only server-side — no gate state, nothing sends.
 *
 * @throws ApiClientError on non-2xx (404 for non-previewable steps)
 * @sideEffects HTTP GET only
 * @idempotent yes
 */
export async function fetchScheduledEmailPreview(
  eventId: string,
  itemId: string,
  stepId: string,
): Promise<ScheduledEmailPreview> {
  return api<ScheduledEmailPreview>(
    `/api/speakers/${encodeURIComponent(eventId)}/${encodeURIComponent(itemId)}/email-preview/${encodeURIComponent(stepId)}`,
  );
}

/**
 * Result of the pre-start past-speaker lookup (server: PreviousSpeakerCheckOut).
 * `already_flagged` = the monday checkbox is already set (no lookup ran);
 * `found` = Personatech shows prior-edition speaking (`editions` = which ones);
 * `not_found` = the lookup ran clean; `unavailable` = the check couldn't run
 * (`reason`: no_email / no_registration_name / not_configured / lookup_failed).
 */
export interface PreviousSpeakerCheck {
  status: 'already_flagged' | 'found' | 'not_found' | 'unavailable';
  editions: string[];
  reason: string | null;
}

/**
 * Read-only past-speaker lookup so the dashboard can SHOW the check before a
 * start. `itemId` for a board speaker, or `email` for manual entry before any
 * row exists. Purely a preview — the authoritative monday checkbox write still
 * happens inside POST /api/speakers/start.
 *
 * @throws ApiClientError on non-2xx (404 flag off / speaker not on board)
 * @sideEffects HTTP GET (one Personatech search server-side)
 * @idempotent yes
 */
export async function checkPreviousSpeaker(
  eventId: string,
  target: { itemId: string } | { email: string },
): Promise<PreviousSpeakerCheck> {
  const params = new URLSearchParams({ eventId });
  if ('itemId' in target) params.set('itemId', target.itemId);
  else params.set('email', target.email);
  return api<PreviousSpeakerCheck>(`/api/speakers/previous-check?${params.toString()}`);
}

/**
 * One candidate Gmail thread the invite could reply onto (server:
 * InviteThreadOut). `connectionId` is the caller's connected mailbox that owns
 * the thread — the invite can only reply from that mailbox. `lastMessageAt` is
 * the thread's last message time (naive-UTC ISO with a trailing Z; null when
 * Gmail omitted it).
 */
export interface InviteThreadCandidate {
  connectionId: string;
  threadId: string;
  subject: string;
  snippet: string;
  lastMessageAt: string | null;
  mailbox: string;
}

/**
 * Lists recent Gmail threads (last year) with a speaker email, across the
 * CALLER's connected mailboxes — powers the kickoff "reply on an existing
 * thread" picker (design §5). One row per thread from its last message; a
 * mailbox that can't be read is silently skipped server-side. ≤10 per mailbox.
 *
 * @param eventId the speaker's event (access-checked server-side)
 * @param email the speaker's email to search for
 * @returns InviteThreadCandidate[] (empty when no threads / no connected Gmail)
 * @throws ApiClientError on non-2xx (404 flag off / no event access)
 * @sideEffects HTTP GET (read-only Gmail API server-side)
 * @idempotent yes
 */
export async function fetchInviteThreads(
  eventId: string,
  email: string,
): Promise<InviteThreadCandidate[]> {
  const params = new URLSearchParams({ eventId, email });
  return api<InviteThreadCandidate[]>(`/api/speakers/threads?${params.toString()}`);
}

/** The picked thread the invite should reply onto (server: InviteThreadRef). */
export interface InviteThreadRef {
  connectionId: string;
  threadId: string;
}

/**
 * Result of POST /api/speakers/start (server: StartOnboardingOut). `itemId` is
 * the monday item the workflow is keyed on — for manual entry it's the row the
 * server just created, so the client can land on `/speakers/{eventId}/{itemId}`.
 */
export interface StartOnboardingResult {
  workflowId: string;
  status: string;
  created: boolean;
  itemId: string;
}

/**
 * Body for POST /api/speakers/start: a board speaker by `itemId`, or manual
 * entry by `name` + `email` — the server creates the monday board row FIRST
 * (the board stays the system of record), then onboards against the new item.
 * Manual entry may carry `company` / `jobTitle` / `ccEmails` (free text, commas
 * or semicolons) / `previousSpeaker`, written onto the new board row; the
 * server rejects them alongside `itemId` (existing rows are edited on monday).
 * `startAt` (default 'invite') starts mid-way for speakers onboarded partly by
 * hand: 'invite_reply' parks on the reply, 'confirmation' skips the invite
 * thread, 'asset_collection' parks on asset receipt, 'tail' arms band B only.
 * `releaseNow` (mid-way starts only) opens band-B gates a mid-way start would
 * otherwise hold: `['promotion']` fires the promotion hold early, and
 * `['card_assets']` opts the card gate out of its park (a plain mid-way start
 * PARKS it, so cutting over a roster queues no approvals — the one-click
 * "Generate speaker card" is the per-speaker opt-in).
 * `inviteThread` (design §5) makes the invite reply onto an existing Gmail
 * thread the reviewer picked (from `fetchInviteThreads`); only valid on a fresh
 * invite (`startAt` unset/'invite') and the connection must be the caller's.
 */
export type StartOnboardingBody =
  | {
      eventId: string;
      itemId: string;
      startAt?: StartAt;
      releaseNow?: string[];
      inviteThread?: InviteThreadRef;
    }
  | {
      eventId: string;
      name: string;
      email: string;
      company?: string;
      jobTitle?: string;
      ccEmails?: string;
      previousSpeaker?: boolean;
      startAt?: StartAt;
      releaseNow?: string[];
      inviteThread?: InviteThreadRef;
    };

/**
 * Kicks off THE onboarding workflow for a speaker (one per speaker per event).
 * The engine opens the invite gate.
 *
 * @param body `{ eventId, itemId }` for a board speaker, or `{ eventId, name, email }` for manual entry
 * @returns StartOnboardingResult ({ workflowId, status, created }) — created:false when onboarding already exists
 * @throws ApiClientError on non-2xx (404 flag off / EVENT_NOT_FOUND / SPEAKER_NOT_FOUND; 502 MONDAY_READ_FAILED / MONDAY_CREATE_FAILED)
 * @sideEffects HTTP POST; may create a monday board row (manual entry); a Workflow row + a background engine run
 * @idempotent yes — re-kicking returns the existing workflow (created:false)
 */
export async function startOnboarding(body: StartOnboardingBody): Promise<StartOnboardingResult> {
  return api<StartOnboardingResult>('/api/speakers/start', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** One row of a bulk start request: the board item + its confirmed entry point. */
export interface BulkStartItem {
  itemId: string;
  startAt: StartAt;
}

/** Per-speaker outcome of a bulk start (server: BulkStartResultOut). */
export interface BulkStartResult {
  itemId: string;
  name: string | null;
  outcome: 'started' | 'existing' | 'not_on_board' | 'not_ready' | 'error';
  workflowId: string | null;
  startAt: string | null;
  error: string | null;
}

/** Result of POST /api/speakers/start-bulk (server: BulkStartOut). */
export interface BulkStartOutcome {
  results: BulkStartResult[];
  startedCount: number;
}

/**
 * Starts onboarding for MANY board speakers at once, each at its own entry
 * point — the roster's "Start remaining" kick-off. The server executes the
 * confirmed list verbatim and NEVER re-invites (any prior attempt → 'existing').
 *
 * @param eventId the roster's event
 * @param items the user-confirmed (itemId, startAt) list (max 200)
 * @returns BulkStartOutcome — one result per requested item, in order
 * @throws ApiClientError on non-2xx (404 flag off / EVENT_NOT_FOUND; 502 MONDAY_READ_FAILED)
 * @sideEffects HTTP POST; creates + dispatches one workflow per started item
 * @idempotent yes — re-posting returns 'existing' for the lot
 */
export async function startOnboardingBulk(
  eventId: string,
  items: BulkStartItem[],
): Promise<BulkStartOutcome> {
  return api<BulkStartOutcome>('/api/speakers/start-bulk', {
    method: 'POST',
    body: JSON.stringify({ eventId, items }),
  });
}
